/* eslint-disable arrow-body-style */
import * as React from "react";
import {Component} from "react";

import {observer} from "mobx-react";

import "./project-filter.scss";

import {Dropdown} from "semantic-ui-react";

import {InputOnChangeData} from "semantic-ui-react/dist/commonjs/elements/Input/Input";

import {ProcessConfiguration} from "../../config/process-configuration";
import {DropdownItemProps} from "semantic-ui-react/dist/commonjs/modules/Dropdown/DropdownItem";
import {ProcessStatusConfiguration} from "../../config/process-status-configuration";
import {Municipality} from "../../models/municipality";
import {Icon} from "../../core/components/icon/icon";
import {Auftragsverwaltung} from "../../stores/auftragsverwaltung-mobx";



export interface ProjectFilterProps {
    auftragsverwaltung: Auftragsverwaltung;
    processType: "gefahrenkarte" | "risikoübersicht";
}

@observer
export class ProjectFilter extends Component<ProjectFilterProps, any> {

    constructor(props: ProjectFilterProps) {
        super(props);
        this.state = {
            loading: false,
            filterOptions: [],
            value: this.props.auftragsverwaltung.ProjectStore.filters || []
        };

        this.handleFilterChange = this.handleFilterChange.bind(this);
    }

    componentDidMount(): void {
        this.loadDomains().then((domains) => {
            this.initFilters(domains);
        });
    }

    componentWillUnmount(): void {}

    render(): React.ReactElement | null {
        const {filterOptions, value} = this.state;

        if (!filterOptions) {
            return null;
        }

        return (
            <Dropdown
                className="project-filter"
                placeholder="Filter"
                fluid
                multiple
                search
                selection
                options={filterOptions}
                onChange={this.handleFilterChange}
                value={value}
                compact
            />
        );
    };

    handleFilterChange(event: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData): void {
        const {auftragsverwaltung} = this.props;

        auftragsverwaltung.ProjectStore.filters = data.value as unknown as string[]; // multi select

        this.setState({
            value: data.value
        });
    };

    private initFilters(domains: Array<Map<number, string> | null>): void {
        const {filterOptions} = this.state;

        this.addProcessFilterOptions(filterOptions);
        this.addTypeFilterOptions(filterOptions, domains[0]);
        this.addMunicipalityFilter();

        this.setState({
            filterOptions
        });
    }

    private addProcessFilterOptions(options: DropdownItemProps[]): void {
        const {auftragsverwaltung, processType} = this.props;

        // add header
        options.push({
            key: "status-filter-header",
            text: "Status",
            value: "status-filter-header",
            label: <Icon name="trafficlight_green" />,
            disabled: true
        });
        options.push({
            key: "status-filter-divider1",
            className: "divider",
            disabled: true
        });
        // add filter for process states. respect all different states of all processes and versions
        auftragsverwaltung.mapViewerConfig.processes.filter((p) => p.type === processType)
            .forEach((pc: ProcessConfiguration) => {
                pc.states.sort(ProcessStatusConfiguration.sortByStatus).forEach((ps: ProcessStatusConfiguration) => {
                    const key = `status-${pc.id}-${pc.version}-${ps.id}`;
                    const text = `Status: ${ps.name}`;
                    const label = <Icon name={ps.icon} />;
                    options.push({
                        key, text, value: key, label
                    });
                });
            });
        options.push({
            key: "status-filter-divider2",
            className: "divider",
            disabled: true
        });
    }

    private addTypeFilterOptions(options: DropdownItemProps[], typeCodes: Map<number, string> | null): void {
        if (!typeCodes) {
            return;
        }

        // add header
        options.push({
            key: "type-filter-header",
            text: "Typ",
            value: "type-filter-header",
            label: <Icon name="elements_tree" />,
            disabled: true
        });
        options.push({
            key: "type-filter-divider1",
            className: "divider",
            disabled: true
        });
        // add filter for process states. respect all different states of all processes and versions
        typeCodes.forEach((description: string, code: number) => {
            const key = `type-${code}`;
            const text = `Typ: ${description}`;
            options.push({
                key, text, value: key
            });
        });
        options.push({
            key: "type-filter-divider2",
            className: "divider",
            disabled: true
        });
    }

    private addMunicipalityFilter(): void {
        const {auftragsverwaltung} = this.props;

        if (auftragsverwaltung.MunicipalityStore.dataStatus === "loaded") {
            const municipalities = [...auftragsverwaltung.MunicipalityStore.items.values()];
            // municipalities are multipart features, so we expect to get multiple entries per municipality
            // --> make unique for select options
            const unique =  [...new Map(municipalities.map((m) => [m.name, m])).values()];
            const sorted = unique.sort(Municipality.sortByName);
            this.addMunicipalityFilterOptions(sorted);
        }
        else {
            auftragsverwaltung.MunicipalityStore.loadItems().then((municipalities) => {
                // municipalities are multipart features, so we expect to get multiple entries per municipality
                // --> make unique for select options
                const unique =  [...new Map(municipalities.map((m) => [m.name, m])).values()];
                const sorted = unique.sort(Municipality.sortByName);
                this.addMunicipalityFilterOptions(sorted);
            });
        }
    }

    /**
     * Add filter options for municipality filter
     * @param municipalities
     * @private
     */
    private addMunicipalityFilterOptions(municipalities: Municipality[]): void {
        const {filterOptions} = this.state;

        if (!municipalities || municipalities.length === 0) {
            return;
        }

        // add header
        filterOptions.push({
            key: "municipality-filter-header",
            text: "Gemeinde",
            value: "municipality-filter-header",
            label: <Icon name="central_bank"/>,
            disabled: true
        });
        filterOptions.push({
            key: "municipality-filter-divider1",
            className: "divider",
            disabled: true
        });
        // add filter for process states. respect all different states of all processes and versions
        municipalities.forEach((municipality) => {
            const key = `municipality-${municipality.bfsNr}`;
            const text = `Gemeinde: ${municipality.bfsName}`;
            filterOptions.push({
                key, text, value: key
            });
        });
        filterOptions.push({
            key: "municipality-filter-divider2",
            className: "divider",
            disabled: true
        });

        this.setState({
            filterOptions
        });
    }

    /**
     * Load domains. Domains are then stored in ActivityStore and are accessible by domainsByField
     *
     * @private
     */
    private loadDomains(): Promise<Array<Map<number, string> | null>> {
        return new Promise((resolve) => {
            const {auftragsverwaltung} = this.props;

            const domainPromises = [
                auftragsverwaltung.getShortDomainCodes("project", "typeId")
            ];

            Promise.all(domainPromises).then((domains) => {
                resolve(domains);
            });
        });
    }

}
